import { CircleCheck } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useSession } from '@/entities/session/hooks/use-session';
import { CategoryIcon } from '@/entities/shared/category/category-icon';
import { DueBadge } from '@/entities/shared/due/due-badge';
import { UrgencyPill } from '@/entities/shared/due/urgency-pill';
import { PriorityBadge } from '@/entities/shared/priority/priority-badge';
import { TaskDoneButton } from '@/entities/task/components/task-done-button';
import { EmptyState } from '@/shared/components/empty-state';
import { IconTile } from '@/shared/components/icon-tile';
import { ListRow } from '@/shared/components/list/list-row';
import { Page } from '@/shared/components/page/page';
import { PageHeader } from '@/shared/components/page/page-header';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatCents } from '@/shared/lib/money/format-cents';
import { formatQuantity } from '@/shared/lib/money/format-quantity';
import { dashboardSummary } from '../helpers/dashboard-summary';
import { useDashboard } from '../hooks/use-dashboard';
import { CategoryCoverage } from './category-coverage';
import { DashboardSection } from './dashboard-section';
import { DashboardStats } from './dashboard-stats';

/** Empty sections are hidden; when nothing needs anything, one line says so. */
export function DashboardScreen() {
  const { t } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();
  const dashboard = useDashboard();
  const { loading, attention, toBuy, low, items } = dashboard;
  const calm = !loading && attention.length + toBuy.length + low.length === 0;
  return (
    <Page>
      <PageHeader
        titleOnPhone
        title={session ? t('Hi, {name}', { name: session.user.display_name }) : t('Dashboard')}
        description={dashboardSummary({
          loading,
          attention: attention.length,
          overdue: dashboard.overdue,
          low: low.length,
        })}
      />
      <DashboardStats dashboard={dashboard} />
      {calm ? (
        <EmptyState
          icon={CircleCheck}
          title={t('All in order')}
          description={t(
            'Nothing is late or due soon, nothing is below its minimum and the shopping list is empty.',
          )}
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <DashboardSection
            title={t('Needs attention')}
            to="/inventory"
            loading={loading}
            count={attention.length}
            className="lg:col-span-2"
          >
            {attention.slice(0, 8).map((row) => (
              <ListRow
                key={row.id}
                onClick={() => navigate(row.to)}
                leading={row.task ? <TaskDoneButton view={row.task} /> : <IconTile icon={row.icon} />}
                title={row.title}
                subtitle={row.subtitle}
                status={<DueBadge due={row.due} />}
              />
            ))}
          </DashboardSection>
          <DashboardSection title={t('Shopping list')} to="/shopping" loading={loading} count={toBuy.length}>
            {toBuy.slice(0, 3).map((view) => (
              <ListRow
                key={view.id}
                onClick={() => navigate(`/shopping?edit=${view.id}`)}
                leading={<CategoryIcon category={view.data.category} />}
                title={view.data.name}
                subtitle={formatQuantity(view.data.quantity, view.data.unit)}
                status={<PriorityBadge priority={view.data.priority} />}
                trailing={
                  view.data.estimated_price_cents != null ? (
                    <span className="text-sm tabular-nums">
                      {formatCents(view.data.estimated_price_cents)}
                    </span>
                  ) : undefined
                }
              />
            ))}
          </DashboardSection>
          <DashboardSection title={t('Low stock')} to="/inventory" loading={loading} count={low.length}>
            {low.slice(0, 5).map((view) => (
              <ListRow
                key={view.id}
                onClick={() => navigate(`/inventory?edit=${view.id}`)}
                leading={<CategoryIcon category={view.data.category} />}
                title={view.data.name}
                subtitle={t('{quantity} left, minimum {minimum}', {
                  quantity: formatQuantity(view.data.quantity, view.data.unit),
                  minimum: formatQuantity(view.data.min_quantity, view.data.unit),
                })}
                status={<UrgencyPill urgency="soon">{t('Restock')}</UrgencyPill>}
              />
            ))}
          </DashboardSection>
        </div>
      )}
      {items && items.length > 0 && <CategoryCoverage items={items} />}
    </Page>
  );
}
